import { Avatar, AvatarImage, AvatarFallback } from '@artstorefronts/ui';
import { Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UserAvatarProps {
  avatarUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  size?: number;
  className?: string;
  isHumanVerified?: boolean;
  isRecentContestWinner?: boolean;
  isDisabled?: boolean;
}

function getInitials(displayName?: string | null, firstName?: string | null, lastName?: string | null): string {
  const name = displayName || [firstName, lastName].filter(Boolean).join(' ') || '';
  if (!name) return '';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getFontSize(size: number): string {
  if (size <= 24) return 'text-[9px]';
  if (size <= 32) return 'text-xs';
  if (size <= 40) return 'text-sm';
  return 'text-base';
}

export function UserAvatar({
  avatarUrl,
  firstName,
  lastName,
  displayName,
  size = 32,
  className = '',
  isHumanVerified = false,
  isRecentContestWinner = false,
  isDisabled = false,
}: UserAvatarProps) {
  const { t } = useTranslation('common');
  const initials = getInitials(displayName, firstName, lastName);
  const name = displayName || [firstName, lastName].filter(Boolean).join(' ') || t('user');

  if (isDisabled) {
    return (
      <div
        className={`rounded-full bg-gray-200 flex items-center justify-center ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <Ban className="text-gray-400" style={{ width: `${size * 0.6}px`, height: `${size * 0.6}px` }} />
      </div>
    );
  }

  const verifiedRing = isHumanVerified ? `${size >= 80 ? 'ring-3' : 'ring-2'} ring-green-500 ring-offset-1` : '';

  const avatarElement = (
    <Avatar
      className={`${verifiedRing} ${className}`.trim()}
      style={{ width: `${size}px`, height: `${size}px` }}
      {...({} as any)}
    >
      {avatarUrl && <AvatarImage src={avatarUrl} alt={name} className="object-cover" {...({} as any)} />}
      <AvatarFallback
        className={`bg-gray-200 text-gray-600 font-medium ${getFontSize(size)}`}
        delayMs={avatarUrl ? 600 : 0}
        {...({} as any)}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  if (isRecentContestWinner) {
    return (
      <div className="relative inline-flex" style={{ width: `${size + 6}px`, height: `${size + 6}px` }}>
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-amber-400 animate-spin-slow" />
        <div className="absolute inset-[3px]">{avatarElement}</div>
      </div>
    );
  }

  return avatarElement;
}
